import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { X, Upload, Image as ImageIcon, GripVertical, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ProductImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxImages?: number;
}

export const ProductImageUpload: React.FC<ProductImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = 5
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const validateFile = (file: File): boolean => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Format non supporté",
        description: "Formats acceptés: JPG, PNG, WEBP, GIF",
        variant: "destructive"
      });
      return false;
    }

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximale est de 5 Mo",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      // Vérification de l'authentification et du rôle vendor
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        toast({
          title: "Authentification requise",
          description: "Vous devez être connecté comme vendeur pour uploader une image.",
          variant: "destructive"
        });
        return null;
      }

      // Optionnel : vérifier le rôle vendor (si accessible dans le profil)
      // const { data: profileData } = await supabase.from('profiles').select('role').eq('id', userData.user.id).single();
      // if (profileData?.role !== 'vendor') {
      //   toast({
      //     title: "Accès refusé",
      //     description: "Seuls les vendeurs peuvent uploader des images de produit.",
      //     variant: "destructive"
      //   });
      //   return null;
      // }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('products')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Erreur d'upload",
        description: "Impossible d'uploader l'image",
        variant: "destructive"
      });
      return null;
    }
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const currentImages = images || [];
    if (currentImages.length >= maxImages) {
      toast({
        title: "Limite atteinte",
        description: `Vous pouvez ajouter maximum ${maxImages} photos`,
        variant: "destructive"
      });
      return;
    }

    const remainingSlots = maxImages - currentImages.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    // Validate all files first
    const validFiles = filesToUpload.filter(validateFile);
    if (validFiles.length === 0) return;

    setUploading(true);

    try {
      const uploadPromises = validFiles.map(file => uploadImage(file));
      const uploadedUrls = await Promise.all(uploadPromises);
      
      const successfulUrls = uploadedUrls.filter((url): url is string => url !== null);
      
      if (successfulUrls.length > 0) {
        const currentImages = images || [];
        onImagesChange([...currentImages, ...successfulUrls]);
        toast({
          title: "Images ajoutées",
          description: `${successfulUrls.length} image(s) ajoutée(s) avec succès`
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async (index: number) => {
    const currentImages = images || [];
    const imageUrl = currentImages[index];
    
    // Try to delete from storage (extract filename from URL)
    try {
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      if (fileName && !imageUrl.includes('unsplash.com')) {
        await supabase.storage
          .from('products')
          .remove([fileName]);
      }
    } catch (error) {
      console.error('Error deleting image from storage:', error);
    }

    // Remove from state
    const newImages = currentImages.filter((_, i) => i !== index);
    onImagesChange(newImages);
    
    toast({
      title: "Image supprimée",
      description: "L'image a été retirée du produit"
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;
    
    const newImages = [...images];
    const draggedImage = newImages[draggingIndex];
    newImages.splice(draggingIndex, 1);
    newImages.splice(index, 0, draggedImage);
    
    onImagesChange(newImages);
    setDraggingIndex(index);
  };

  const handleImageDragEnd = () => {
    setDraggingIndex(null);
  };

  return (
    <div className="space-y-4">
      <Label>Photos du produit (Maximum {maxImages})</Label>
      
      {/* Image Preview Grid with Drag & Drop */}
      {images && images.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            📷 Glissez-déposez les images pour changer l'ordre d'affichage
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {images.map((imageUrl, index) => (
              <div
                key={index}
                draggable
                onDragStart={(e) => handleImageDragStart(e, index)}
                onDragOver={(e) => handleImageDragOver(e, index)}
                onDragEnd={handleImageDragEnd}
                className={`relative aspect-square rounded-lg border-2 overflow-hidden group cursor-move transition-all ${
                  draggingIndex === index ? 'opacity-50 scale-95' : 'hover:border-primary'
                }`}
              >
                <div className="absolute top-2 left-2 bg-black/60 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <GripVertical className="h-4 w-4" />
                </div>
                {index === 0 && (
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1 z-10">
                    <Star className="h-3 w-3 fill-current" />
                    <span>Principal</span>
                  </div>
                )}
                <img
                  src={imageUrl}
                  alt={`Produit ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  onClick={() => handleRemoveImage(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  #{index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Area */}
      {(!images || images.length < maxImages) && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
            disabled={uploading}
          />
          
          <div className="flex flex-col items-center gap-2">
            {uploading ? (
              <>
                <Upload className="h-8 w-8 text-muted-foreground animate-bounce" />
                <p className="text-sm text-muted-foreground">Upload en cours...</p>
              </>
            ) : (
              <>
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Glissez-déposez vos images ici
                </p>
                <p className="text-xs text-muted-foreground">
                  ou
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Parcourir
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  JPG, PNG, WEBP, GIF (max 5MB) - {images.length}/{maxImages} photos
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
